import {
  isGradientStrategy,
  isFullRangeStrategy,
  isOverlappingStrategy,
  isPaused,
} from 'components/strategies/common/utils';
import { FC, forwardRef, ReactNode, useState } from 'react';
import { useModal } from 'hooks/useModal';
import { useNavigate, useSearch } from 'libs/routing';
import { DropdownMenu, MenuButtonProps } from 'components/common/dropdownMenu';
import { Tooltip } from 'components/common/tooltip/Tooltip';
import MoreHorizIcon from 'assets/icons/more_horiz.svg?react';
import {
  StrategyEditOptionId,
  getTooltipTextByStrategyEditOptionsId,
} from './utils';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useGetVoucherOwner } from 'libs/queries/chain/voucher';
import { cn } from 'utils/helpers';
import { useIsStrategyOwner } from 'hooks/useIsStrategyOwner';
import { isDisposableStrategy } from 'components/strategies/common/utils';
import {
  getEditBudgetPage,
  getEditPricesPage,
} from 'components/strategies/edit/utils';
import { toPairSlug } from 'utils/pairSearch';
import { AnyStrategy } from 'components/strategies/common/types';
import { useDuplicate } from 'components/strategies/create/useDuplicateStrategy';
import { usePairs } from 'hooks/usePairs';
import { getRoundedSpread } from 'components/strategies/overlapping/utils';
import config from 'config';

type itemsType = {
  id: StrategyEditOptionId;
  name: string;
  action?: () => void;
  disabled?: boolean;
};

type separatorCounterType = number;

interface Props {
  strategy: AnyStrategy;
  button: (props: ManageButtonProps) => ReactNode;
}

export const StrategyBlockManage: FC<Props> = (props) => {
  const { strategy } = props;
  const [manage, setManage] = useState(false);
  const { openModal } = useModal();
  const navigate = useNavigate();
  const { search } = useSearch({ strict: false });
  const { getType } = usePairs();
  const type = getType(search ?? '');
  const duplicate = useDuplicate();
  const { base, quote, buy, sell } = strategy;

  const isOwn = useIsStrategyOwner(strategy.id);

  const owner = useGetVoucherOwner(manage ? strategy.id : undefined);

  const isOverlapping = isOverlappingStrategy(strategy);
  const isDisposable = isDisposableStrategy(strategy);
  const isGradient = isGradientStrategy(strategy);

  const items: (itemsType | separatorCounterType)[] = [];

  if (!isGradient && !isPaused(strategy)) {
    items.push({
      id: 'duplicateStrategy',
      name: 'Duplicate strategy',
      action: () => {
        if (isFullRangeStrategy(base, quote, buy, sell)) {
          duplicate(strategy);
        } else {
          openModal('duplicateStrategy', { strategy });
        }
      },
    });
  }

  if (!isPaused(strategy)) {
    items.push({
      id: 'trade',
      name: 'Trade this pair',
      action: () => {
        navigate({
          to: '/trade/market',
          search: {
            base: strategy.base.address,
            quote: strategy.quote.address,
          },
        });
      },
    });
  }

  if (config.ui.showSimulator && !isGradient && !isDisposable) {
    items.push({
      id: 'simulate',
      name: 'Simulate',
      action: () => {
        if (isOverlapping) {
          navigate({
            to: '/simulate/overlapping',
            search: {
              base: strategy.base.address,
              quote: strategy.quote.address,
              buyMin: strategy.buy.min,
              sellMax: strategy.sell.max,
              spread: getRoundedSpread(strategy).toString(),
            },
          });
        } else {
          navigate({
            to: '/simulate/recurring',
            search: {
              base: strategy.base.address,
              quote: strategy.quote.address,
              buyMin: strategy.buy.min,
              buyMax: strategy.buy.max,
              buyBudget: strategy.buy.budget,
              buyIsRange: strategy.buy.max !== strategy.buy.min,
              sellMin: strategy.sell.min,
              sellMax: strategy.sell.max,
              sellBudget: strategy.sell.budget,
              sellIsRange: strategy.sell.max !== strategy.sell.min,
            },
          });
        }
      },
    });
  }

  if (!isOwn && type !== 'wallet') {
    items.push({
      id: 'walletOwner',
      name: "View owner's strategies",
      action: () => {
        navigate({
          to: '/explore',
          search: { search: owner.data ?? '' },
        });
      },
      disabled: !owner.data,
    });
  }

  if (type === 'wallet') {
    items.push({
      id: 'explorePair',
      name: 'Explore pair',
      action: () => {
        const slug = toPairSlug(strategy.base, strategy.quote);
        navigate({
          to: '/explore',
          search: { search: slug },
        });
      },
    });
  }

  if (isOwn) {
    if (!isGradientStrategy(strategy)) {
      const editPrices = getEditPricesPage(strategy, 'editPrices');

      items.push({
        id: 'editPrices',
        name: 'Edit prices',
        action: () => {
          navigate({
            to: editPrices.to,
            search: editPrices.search,
            params: { strategyId: strategy.id },
          });
        },
      });

      const deposit = getEditBudgetPage(strategy, 'deposit');
      // separator
      items.push(0);
      items.push({
        id: 'depositFunds',
        name: 'Deposit funds',
        action: () => {
          navigate({
            to: deposit.to,
            search: deposit.search,
            params: { strategyId: strategy.id },
          });
        },
      });

      if (strategy.status !== 'noBudget') {
        items.push({
          id: 'withdrawFunds',
          name: 'Withdraw funds',
          action: () => {
            openModal('confirmWithdrawStrategy', { strategy });
          },
        });
      }
    }

    // separator
    if (items.length) {
      items.push(1);
    }

    if (strategy.status === 'active' && !isGradient) {
      items.push({
        id: 'pauseStrategy',
        name: 'Pause strategy',
        action: () => {
          openModal('confirmPauseStrategy', { strategy });
        },
      });
    }

    if (!isGradient && strategy.status === 'paused') {
      const renew = getEditPricesPage(strategy, 'renew');
      items.push({
        id: 'renewStrategy',
        name: 'Renew strategy',
        action: () => {
          navigate({
            to: renew.to,
            search: renew.search,
            params: { strategyId: strategy.id },
          });
        },
      });
    }

    items.push({
      id: 'deleteStrategy',
      name: 'Withdraw & delete',
      action: () => {
        openModal('confirmDeleteStrategy', { strategy });
      },
    });
  }

  return (
    <DropdownMenu
      isOpen={manage}
      setIsOpen={setManage}
      className="z-10 w-fit p-10"
      button={(attr) => {
        return props.button({
          ...attr,
          role: 'menuitem',
          'aria-label': 'Manage strategy',
          'data-testid': 'manage-strategy-btn',
        });
      }}
    >
      <ul role="menu" data-testid="manage-strategy-dropdown">
        {items.map((item) => {
          if (typeof item === 'number') {
            return <hr key={item} className="border  border-main-700 my-10" />;
          }

          const { name, id, action, disabled } = item;

          return (
            <ManageItem
              key={id}
              title={name}
              setManage={setManage}
              action={action}
              id={id}
              isOwn={isOwn}
              disabled={disabled}
            />
          );
        })}
      </ul>
    </DropdownMenu>
  );
};

interface ManageButtonProps extends MenuButtonProps {
  role: 'menuitem';
  'aria-label': string;
  'data-testid': string;
}

export const ManageButtonIcon = forwardRef<
  HTMLButtonElement,
  ManageButtonProps
>(function ManageButtonIcon(props, ref) {
  return (
    <button
      {...props}
      ref={ref}
      className="btn-on-surface size-38 rounded-md grid place-items-center p-0"
    >
      <MoreHorizIcon className="size-24" />
    </button>
  );
});

const ManageItem: FC<{
  title: string;
  id: StrategyEditOptionId;
  setManage: (flag: boolean) => void;
  action?: () => void;
  isOwn?: boolean;
  disabled?: boolean;
}> = ({ title, id, setManage, action, isOwn, disabled }) => {
  const tooltipText = getTooltipTextByStrategyEditOptionsId(isOwn)?.[id];
  const { belowBreakpoint } = useBreakpoints();

  const Content = () => {
    return (
      <button
        role="menuitem"
        type="button"
        onClick={() => {
          if (action) action();
          setManage(false);
        }}
        disabled={disabled}
        className={cn('rounded-sm w-full p-12 text-left', {
          'cursor-not-allowed': disabled,
          'opacity-60': disabled,
          'hover:bg-main-900/40': !disabled,
        })}
        data-testid={`manage-strategy-${id}`}
      >
        {title}
      </button>
    );
  };

  if (tooltipText) {
    return (
      <Tooltip disabled={belowBreakpoint('md')} element={tooltipText}>
        <li role="none">
          <Content />
        </li>
      </Tooltip>
    );
  }

  return (
    <li role="none">
      <Content />
    </li>
  );
};
